import { useEffect, useState } from 'react';
import { eventBus } from '@/shared/EventBus';
import { Icon } from './Icon';
import { eggAssetUrl, petAssetUrl } from '@/assets/paths';
import { formatNumberCompact } from '@/utils/formatNumber';
import { PETS_BY_ID } from '@/config/pets.config';
import { EGGS_BY_ID } from '@/config/eggs.config';

/**
 * Modal nagrody za pokonanie Strażnika (World Boss, ATL-2). Zastępuje stary,
 * tekstowy panel rysowany w Phaserze — tutaj prawdziwe grafiki: ikony monet/gemów
 * (`<Icon>` PNG), GRAFIKA Jajka Królewskiego Atlantydy i ewentualnie obrazek Władcy.
 *
 * Sterowany eventem `sanctum:reward` (scena → React). Wartości są już zaksięgowane
 * serwerowo — modal tylko prezentuje. „Wróć do Atlantydy" emituje `sanctum:exit`
 * (scena teleportuje z powrotem). Self-gate: null gdy brak nagrody.
 */
type RewardData = {
  coins: string;
  gems: number;
  royalEggs: number;
  royalEggId: string | null;
  rulerPetId: string | null;
  firstClear: boolean;
  deferred: boolean;
};

export function BossRewardModal() {
  const [reward, setReward] = useState<RewardData | null>(null);

  useEffect(() => {
    return eventBus.on('sanctum:reward', (data) => setReward(data));
  }, []);

  if (!reward) return null;

  const coins = reward.coins;
  const rulerDef = reward.rulerPetId ? PETS_BY_ID[reward.rulerPetId] : undefined;
  // `royalEggId` to ID jajka (np. „egg-atlantis-royal"); grafika używa textureKey
  // (np. „egg_atlantis_royal") → mapujemy przez config, z bezpiecznym fallbackiem.
  const eggKey =
    (reward.royalEggId ? EGGS_BY_ID[reward.royalEggId]?.art.textureKey : undefined) ??
    'egg_atlantis_royal';

  const close = () => {
    setReward(null);
    eventBus.emit('sanctum:exit', {});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-auto bg-black/65 backdrop-blur-sm">
      <div className="relative w-[min(92vw,460px)] rounded-3xl border-2 border-amber-300/80 bg-gradient-to-b from-[#0c3a44] to-[#06222a] shadow-2xl px-6 py-6 text-center text-amber-50 animate-trade-pop">
        {/* Tytuł */}
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-amber-200 drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
          STRAŻNIK POKONANY
        </h2>
        <p className="mt-1 text-sm text-cyan-200/80">Świątynia Atlantydy została wyzwolona!</p>

        {/* HERO: Jajko Królewskie z liczbą */}
        {reward.royalEggs > 0 && (
          <div className="mt-4 flex flex-col items-center">
            <div className="relative">
              <div className="absolute inset-0 -z-10 blur-2xl bg-cyan-300/30 rounded-full" />
              <img
                src={eggAssetUrl(eggKey)}
                alt="Jajko Królewskie Atlantydy"
                className="w-28 h-28 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)] animate-egg-float"
                draggable={false}
              />
              <span className="absolute -bottom-1 -right-1 bg-amber-400 text-[#06222a] text-base font-extrabold rounded-full w-9 h-9 flex items-center justify-center shadow-lg border-2 border-white">
                ×{reward.royalEggs}
              </span>
            </div>
            <span className="mt-2 text-sm font-bold text-amber-100">Królewskie Jajko Atlantydy</span>
          </div>
        )}

        {/* Władca (rzadki bonus) */}
        {rulerDef && (
          <div className="mt-3 flex flex-col items-center rounded-2xl bg-cyan-400/10 border border-cyan-300/50 py-2 px-3">
            <img
              src={petAssetUrl(rulerDef.art.textureKey, rulerDef.origin?.biomeId ?? 'biome-atlantis')}
              alt={rulerDef.name.pl}
              className="w-20 h-20 object-contain drop-shadow-[0_3px_8px_rgba(0,0,0,0.5)]"
              draggable={false}
            />
            <span className="mt-1 text-base font-extrabold text-cyan-100">WŁADCA: {rulerDef.name.pl}!</span>
          </div>
        )}

        {/* Waluty — ikony + liczby */}
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="flex items-center gap-1.5 rounded-2xl bg-black/25 px-3 py-2">
            <Icon src="/ui/ui_coin.webp" fallback="🪙" alt="monety" className="w-7 h-7" />
            <span className="text-lg font-extrabold text-amber-200">+{formatNumberCompact(coins)}</span>
          </div>
          {reward.gems > 0 && (
            <div className="flex items-center gap-1.5 rounded-2xl bg-black/25 px-3 py-2">
              <Icon src="/ui/ui_gem.webp" fallback="💎" alt="gemy" className="w-7 h-7" />
              <span className="text-lg font-extrabold text-cyan-200">+{formatNumberCompact(reward.gems)}</span>
            </div>
          )}
        </div>

        {/* Pierwsze zwycięstwo */}
        {reward.firstClear && (
          <div className="mt-3 rounded-2xl bg-amber-400/15 border border-amber-300/60 py-2 px-3 text-sm font-bold text-amber-100">
            Pierwsze zwycięstwo — gwarantowany Władca!
          </div>
        )}

        {/* Nagroda odłożona (pełne inventory) */}
        {reward.deferred && (
          <p className="mt-3 text-xs text-cyan-200/80">
            Jajka/Władca odłożone — zrób miejsce, odbierzesz po powrocie do Atlantydy.
          </p>
        )}

        <button
          type="button"
          onClick={close}
          className="mt-5 w-full rounded-2xl bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 active:scale-95 text-[#1a0e06] font-extrabold text-lg py-3 shadow-lg transition-all"
        >
          Wróć do Atlantydy
        </button>
      </div>
    </div>
  );
}
